import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'

const Payment = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [paymentInfo, setPaymentInfo] = useState({
    slip: null,
    date: '',
    time: '',
    accountName: '',
    bank: ''
  })
  const [previewSlip, setPreviewSlip] = useState(null)
  const [activePaymentMethod, setActivePaymentMethod] = useState(null)
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountedAmount, setDiscountedAmount] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(null);

  const plans = {
    monthly: {
      name: 'สมัคร VIP 1 เดือน',
      price: 799,
      type: 'monthly',
      id: 1,
      description: 'ใช้งานได้ 1 เดือน ต่ออายุเดือนต่อไปแค่ 99฿'
    },
    renewal: {
      name: 'ต่ออายุ VIP 1 เดือน',
      price: 99,
      type: 'renewal',
      id: 3,
      description: 'สำหรับสมาชิกที่เคยสมัครแล้วเท่านั้น'
    },
    yearly: {
      name: 'สมัคร VIP 1 ปี',
      price: 1399,
      type: 'yearly',
      id: 2,
      description: 'ประหยัดกว่า จากปกติ 1688.-'
    },
    yearlyRenewal: {
      name: 'VIP ตลอดชีพ',
      price: 2399,
      type: 'lifetime',
      id: 4,
      description: 'จ่ายครั้งเดียวจบ'
    }
  }

  useEffect(() => {
    const plan = searchParams.get('plan')
    // ถ้า plan เป็น lifetime ให้ใช้ yearlyRenewal แทน
    const planKey = plan === 'lifetime' ? 'yearlyRenewal' : plan
    setSelectedPlan(plans[planKey])
  }, [searchParams])

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    if (appliedCoupon) {
      const discount = calculateDiscount(plan.price, appliedCoupon);
      setDiscountedPrice(plan.price - discount);
    } else {
      setDiscountedPrice(null);
    }
  };

  const calculateDiscount = (price, coupon) => {
    if (!coupon) return 0;
    
    let discount = 0;
    if (coupon.discount_type === 'fixed') {
      discount = coupon.discount_amount;
    } else {
      discount = (price * coupon.discount_amount) / 100;
      if (coupon.max_discount) {
        discount = Math.min(discount, coupon.max_discount);
      }
    }
    return discount;
  };

  const handlePayment = async () => {
    try {
      console.log('Selected Plan:', selectedPlan)
      console.log('Is Payment Info Complete:', isPaymentInfoComplete())
      
      if (!selectedPlan || !isPaymentInfoComplete()) {
        console.log('Validation failed')
        return
      }

      // 1. อัพโหลดรูปสลิป
      const slipFile = paymentInfo.slip
      const fileExt = slipFile.name.split('.').pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('payment_slips')
        .upload(fileName, slipFile)

      if (uploadError) throw uploadError

      const finalPrice = Math.floor(selectedPlan.price - (appliedCoupon ? calculateDiscount(selectedPlan.price, appliedCoupon) : 0));
      const finalDiscount = Math.floor(appliedCoupon ? calculateDiscount(selectedPlan.price, appliedCoupon) : 0);

      // 2. บันทึกข้อมูลการชำระเงิน
      const { data, error: paymentError } = await supabase
        .from('payments')
        .insert({
          user_id: user.id,
          package_id: selectedPlan.id,
          amount: finalPrice,
          transfer_date: paymentInfo.date,
          transfer_time: paymentInfo.time,
          bank_name: paymentInfo.bank,
          account_name: paymentInfo.accountName,
          slip_image: fileName,
          payment_status: 'pending',
          coupon_code: appliedCoupon?.code,
          discount_amount: finalDiscount
        })
        .select()
        .single()

      if (paymentError) throw paymentError

      // อัพเดทจำนวนการใช้คูปอง
      if (appliedCoupon) {
        const { error: couponError } = await supabase
          .from('coupons')
          .update({ usage_count: appliedCoupon.usage_count + 1 })
          .eq('id', appliedCoupon.id)

        if (couponError) throw couponError
      }

      // ำทางไปยังหน้า PaymentSuccess พร้อมส่ง ID
      navigate(`/payment-success?id=${data.id}`)

    } catch (error) {
      console.error('Error processing payment:', error)
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
    }
  }

  const handleSlipUpload = (e) => {
    const file = e.target.files[0]
    const maxSize = 2 * 1024 * 1024 // 2MB in bytes

    if (file) {
      // ตรวจสอบขนาดไฟล์
      if (file.size > maxSize) {
        alert('ขนาดไฟล์ต้องไม่เกิน 2MB')
        // รีเซ็ตค่า input
        e.target.value = ''
        return
      }

      setPaymentInfo(prev => {
        console.log('Updated payment info with file:', { ...prev, slip: file })
        return { ...prev, slip: file }
      })
      setPreviewSlip(URL.createObjectURL(file))
    }
  }

  const handlePaymentInfoChange = (e) => {
    const { name, value } = e.target
    setPaymentInfo(prev => ({ ...prev, [name]: value }))
  }

  const isPaymentInfoComplete = () => {
    console.log('Payment Info:', {
      slip: !!paymentInfo.slip,
      date: paymentInfo.date,
      time: paymentInfo.time,
      accountName: paymentInfo.accountName,
      bank: paymentInfo.bank
    })
    
    return paymentInfo.slip && 
           paymentInfo.date && 
           paymentInfo.time && 
           paymentInfo.accountName && 
           paymentInfo.bank
  }

  const validateCoupon = async () => {
    try {
      setCouponError('');
      
      if (!selectedPlan) {
        setCouponError('กรุณาเลือกแพ็กเกจก่อนใช้คูปอง');
        return;
      }
      
      if (!couponCode.trim()) {
        setCouponError('กรุณากรอกรหัสคูปอง');
        return;
      }

      const { data: coupon, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', couponCode.trim())
        .eq('is_active', true)
        .single();

      if (error) throw error;

      if (!coupon) {
        setCouponError('ไม่พบคูปองนี้');
        return;
      }

      // ตรวจสอบวันหมดอายุ
      if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
        setCouponError('คูปองหมดอายุแล้ว');
        return;
      }

      // ตรวจสอบจำนวนการใช้งาน
      if (coupon.usage_count >= coupon.usage_limit) {
        setCouponError('คูปงถูกใช้งานครบแล้ว');
        return;
      }

      // ตรวจสอบยอดขั้นต่ำ
      if (coupon.min_purchase > selectedPlan.price) {
        setCouponError(`ต้องมียอดสั่งซื้อขั้นต่ำ ${coupon.min_purchase}฿`);
        return;
      }

      setAppliedCoupon(coupon);

      // คำนวณราคาหลังส่วนลด
      const discount = calculateDiscount(selectedPlan.price, coupon);
      setDiscountedPrice(selectedPlan.price - discount);

    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError('เกิดข้อผิดพลาด กรุณาลองใหม่');
    }
  };

  useEffect(() => {
    if (selectedPlan && appliedCoupon) {
      const discount = calculateDiscount(selectedPlan.price, appliedCoupon);
      setDiscountedAmount(discount);
    }
  }, [selectedPlan, appliedCoupon]);

  const setCurrentDateTime = () => {
    const now = new Date()
    const currentDate = now.toISOString().split('T')[0] // Format: YYYY-MM-DD
    const currentTime = now.toTimeString().slice(0,5)   // Format: HH:MM
    
    setPaymentInfo(prev => ({
      ...prev,
      date: currentDate,
      time: currentTime
    }))
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>ชำระเงิน</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-3xl mx-auto p-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">ชำระเงิน</h1>
            <p className="text-gray-400">กรุณาเลือกแพ็กเกจที่ต้องการ</p>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">เลือกแพ็กเกจ</h2>
            <div className="space-y-3">
              {Object.entries(plans).map(([key, plan]) => {
                const isSelected = selectedPlan?.type === plan.type;
                const priceToShow = isSelected && discountedPrice !== null ? discountedPrice : plan.price;

                return (
                  <div
                    key={key}
                    onClick={() => handleSelectPlan(plan)}
                    className={`
                      flex justify-between items-center p-4 rounded-lg
                      cursor-pointer ${
                        isSelected 
                          ? 'bg-yellow-500/20 border-2 border-yellow-500' 
                          : 'bg-gray-800 border-2 border-transparent hover:border-gray-700'
                      }
                      transition-all
                    `}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`
                        w-5 h-5 rounded-full border-2
                        ${isSelected 
                          ? 'border-yellow-500 bg-yellow-500' 
                          : 'border-gray-500'
                        }
                        flex items-center justify-center
                      `}>
                        {isSelected && (
                          <div className="w-2 h-2 rounded-full bg-black" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium">แพ็กเกจ {plan.name}</h3>
                        <p className="text-sm text-gray-400">
                          {plan.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold">
                        {priceToShow}฿
                      </div>
                      {isSelected && appliedCoupon && (
                        <div className="text-sm text-green-400">
                          ส่วนลด: {calculateDiscount(plan.price, appliedCoupon)}฿
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">คูปองส่วนลด</h2>
            <div className="flex gap-4">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="กรอกรหัสคูปอง"
                className="flex-grow px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-yellow-500"
              />
              <button
                onClick={validateCoupon}
                className="px-6 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors"
              >
                ใช้คูปอง
              </button>
            </div>
            {couponError && (
              <p className="mt-2 text-sm text-red-400">{couponError}</p>
            )}
            {appliedCoupon && (
              <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 font-medium">ใช้คูปองสำเร็จ!</p>
                    <p className="text-sm text-gray-400 mt-1">
                      ส่วนลด: {discountedAmount}฿
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setAppliedCoupon(null);
                      setDiscountedAmount(0);
                      setCouponCode('');
                    }}
                    className="text-gray-400 hover:text-gray-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-900 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">วิธีการชำระเงิน</h2>
            <div className="space-y-4">
              <div className="w-full bg-gray-800 rounded-lg">
                <button 
                  onClick={() => setActivePaymentMethod(activePaymentMethod === 'promptpay' ? null : 'promptpay')}
                  className="w-full p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">P</span>
                    </div>
                    <span>Promptpay</span>
                  </div>
                  <svg 
                    className={`w-5 h-5 transition-transform ${activePaymentMethod === 'promptpay' ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Promptpay Details */}
                {activePaymentMethod === 'promptpay' && (
                  <div className="p-4 border-t border-gray-700">
                    <div className="flex flex-col items-center gap-4">
                      <img 
                        src="/lnw1qr.png" 
                        alt="Promptpay QR Code" 
                        className="w-48 h-48 bg-white p-2 rounded-lg"
                      />
                      <div className="text-center">
                        <p className="text-sm text-gray-400">สแกน QR Code เพื่อชระเงิน</p>
                        <p className="mt-2">
                          <span className="text-gray-400">ชื่อบัญชี:</span>
                          <span className="ml-2">วิรุฬห์</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="w-full bg-gray-800 rounded-lg">
                <button 
                  onClick={() => setActivePaymentMethod(activePaymentMethod === 'bank' ? null : 'bank')}
                  className="w-full p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">B</span>
                    </div>
                    <span>โอนผ่านธนาคาร</span>
                  </div>
                  <svg 
                    className={`w-5 h-5 transition-transform ${activePaymentMethod === 'bank' ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Bank Details */}
                {activePaymentMethod === 'bank' && (
                  <div className="p-4 border-t border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-8 bg-white rounded flex items-center justify-center overflow-hidden">
                        <img 
                          src="/kbank-logo.png" 
                          alt="ธนาคารกสิกรไทย" 
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'block'
                          }}
                        />
                        <span 
                          className="text-green-600 font-bold hidden"
                          style={{ fontSize: '0.7rem' }}
                        >
                          KBANK
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">ธนาคารกสิกรไทย</p>
                        <p className="text-sm text-gray-400">วิรุฬห์</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="font-mono">076-8-55797-7</p>
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText('0768557977')
                              alert('คัดลกเลขบัญชีแล้ว')
                            }}
                            className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                          >
                            คัดลอก
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">ข้อมูลการโอนเงิน</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  สลิปการโอนงิน * <span className="text-xs text-gray-500">(ไฟล์ขนาดไม่เกิน 2MB)</span>
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSlipUpload}
                    className="hidden"
                    id="slip-upload"
                  />
                  <label
                    htmlFor="slip-upload"
                    className="px-4 py-2 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors"
                  >
                    เลือกไฟล์
                  </label>
                  {previewSlip && (
                    <div className="relative">
                      <img 
                        src={previewSlip} 
                        alt="สลิปการโอนเงิน" 
                        className="h-20 w-auto rounded"
                      />
                      <button
                        onClick={() => {
                          setPreviewSlip(null)
                          setPaymentInfo(prev => ({ ...prev, slip: null }))
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-5 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    วันที่โอน *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={paymentInfo.date}
                    onChange={handlePaymentInfoChange}
                    className="w-full px-4 py-2 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>

                <div className="flex items-end justify-center">
                  <button
                    onClick={setCurrentDateTime}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    ใช้เวลาปัจจุบัน
                  </button>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    เวลาที่โอน *
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={paymentInfo.time}
                    onChange={handlePaymentInfoChange}
                    className="w-full px-4 py-2 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    ชื่อบัญชีที่โอน *
                  </label>
                  <input
                    type="text"
                    name="accountName"
                    value={paymentInfo.accountName}
                    onChange={handlePaymentInfoChange}
                    placeholder="ชื่อ-นามสกุล"
                    className="w-full px-4 py-2 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    ธนาคารที่โอน *
                  </label>
                  <select
                    name="bank"
                    value={paymentInfo.bank}
                    onChange={handlePaymentInfoChange}
                    className="w-full px-4 py-2 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">เลือกธนาคาร</option>
                    <option value="kbank">กสิกรไทย</option>
                    <option value="scb">ไทยพาณิชย์</option>
                    <option value="bbl">กรุงเทพ</option>
                    <option value="ktb">กรุงไทย</option>
                    <option value="bay">กรุงศรี</option>
                    <option value="ttb">ทีทีบี</option>
                    <option value="gsb">ออมสิน</option>
                    <option value="kkp">เกียรตินาคิน</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <button 
            onClick={handlePayment}
            disabled={!selectedPlan || !isPaymentInfoComplete()}
            className={`
              w-full py-4 rounded-lg font-semibold transition-colors
              ${selectedPlan && isPaymentInfoComplete()
                ? 'bg-yellow-500 hover:bg-yellow-400 text-black' 
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {!selectedPlan 
              ? 'กรุณาเลือกแพ็กเกจ'
              : !isPaymentInfoComplete()
                ? 'กรุณากรอกข้อมูลให้ครบถ้วน'
                : `แจ้งชำระเงิน ${(selectedPlan.price - (appliedCoupon ? calculateDiscount(selectedPlan.price, appliedCoupon) : 0)).toLocaleString()}฿ ${
                    appliedCoupon ? `(ลด ${calculateDiscount(selectedPlan.price, appliedCoupon)}฿)` : ''
                  }`
            }
          </button>

          <p className="text-center text-sm text-gray-400 mt-6">
            กลังจากได้รับการแจ้งชำระเงินแล้ว รอตรวจสอบไม่เกิน 24 ชั่วโมง จะได้รับวันใช้งานอัตโนมัติ
          </p>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Payment